import { useCallback, useState } from 'react';
import { ActionIcon, Button, Group, NativeSelect, TextInput, Text } from '@mantine/core';
import { IconSend, IconTrash, IconUserPlus } from '@tabler/icons-react';
import { z } from 'zod';

type Props = {
  onSubmit: (members: Invitation[]) => Promise<void>;
};

// TODO consider using mantine form or react-hook-form
const schema = z.object({
  email: z.string().email(),
  role: z.enum(['admin', 'user']),
});

export type Invitation = z.infer<typeof schema>;

const emailSchema = z.string().email();
const isEmail = (email: string) => emailSchema.safeParse(email).success;

export function UserInviteForm({ onSubmit }: Props) {
  const [members, setMembers] = useState<Invitation[]>([{ email: '', role: 'user' }]);
  const [loading, setLoading] = useState(false);
  const isValid = members.every((member) => schema.safeParse(member).success);

  const handleEmailChange = useCallback(
    (index: number, value: string) => {
      const nextMembers = [...members];
      nextMembers[index].email = value;
      setMembers(nextMembers);
    },
    [members, setMembers]
  );

  const handleRoleChange = useCallback(
    (index: number, value: Invitation['role']) => {
      const nextMembers = [...members];
      nextMembers[index].role = value;
      setMembers(nextMembers);
    },
    [members, setMembers]
  );

  const addMember = useCallback(() => {
    setMembers([...members, { email: '', role: 'user' }]);
  }, [members, setMembers]);

  const removeMember = useCallback(
    (index: number) => {
      const nextMembers = [...members];
      nextMembers.splice(index, 1);
      setMembers(nextMembers);
    },
    [members, setMembers]
  );

  const submit = (members: Invitation[]) => {
    setLoading(true);
    return onSubmit(members).finally(() => setLoading(false));
  };

  return (
    <>
      {members.map((member, index) => (
        <Group key={index} align="end" mb="sm">
          <TextInput
            type="email"
            // 狭い幅ではメールを 1 行使い、権限と削除を次の行に送る
            w={{ base: '100%', sm: '50%' }}
            maw="350px"
            label={index === 0 && 'Eメール'}
            value={member.email}
            onChange={(e) => handleEmailChange(index, e.target.value)}
            error={!member.email || isEmail(member.email) ? null : 'Invalid email'}
            inputWrapperOrder={['label', 'error', 'input', 'description']}
            withErrorStyles={false}
          />
          <NativeSelect
            w={{ base: 'calc(100% - 50px)', sm: '30%' }}
            maw="200px"
            label={index === 0 && '権限'}
            data={[
              { label: '一般ユーザ', value: 'user' },
              { label: 'テナント管理者', value: 'admin' },
            ]}
            value={member.role}
            onChange={(e) => handleRoleChange(index, e.currentTarget.value as Invitation['role'])}
          />
          <ActionIcon size="lg" variant="outline" color="gray" onClick={() => removeMember(index)}>
            <IconTrash style={{ width: '60%', height: '60%' }} stroke={1.5} />
          </ActionIcon>
        </Group>
      ))}
      <Group className="my-10">
        <Button
          onClick={addMember}
          variant="outline"
          color="grey"
          leftSection={<IconUserPlus style={{ width: '70%', height: '70%' }} stroke={1.5} />}
        >
          招待先を追加
        </Button>
        <Button
          leftSection={
            !loading && <IconSend style={{ width: '70%', height: '70%' }} stroke={1.5} />
          }
          onClick={() => submit(members)}
          disabled={!isValid}
          loading={loading}
        >
          招待を送る
        </Button>
      </Group>
      <Text size="xs">※ 反映までには数分かかる場合があります。</Text>
    </>
  );
}
